import { FC, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import "./style/homepage.css"
import { useSelector } from "react-redux"
import { ISettingTypeState } from "src/app/modules/siteSettings/components/settings"
import { isEmpty } from "lodash"
import {
  HiUserAdd,
  HiSearch,
  HiDocumentText,
  HiDocumentReport,
  HiDatabase,
  HiArchive,
  HiChartPie,
  HiShoppingBag,
} from "react-icons/hi"
import { MdBookOnline, MdBusiness } from "react-icons/md"
import useGetMasterDataStore from "src/cms/hooks/get/masterData/usePostMasterData"
import { FaShippingFast } from "react-icons/fa"

interface CardDef {
  title: string
  route: string
  icon: JSX.Element
  description: string
  accent: string
}

const MODULE_ICON_MAP: Record<string, JSX.Element> = {
  "New CIF New Account": <HiUserAdd size={26} />,
  "New CIF Only": <HiUserAdd size={26} />,
  "Centralized Corporate Account": <MdBusiness size={26} />,
  "RO Account Opening (Branch)": <FaShippingFast size={26} />,
  "RO Account Opening": <FaShippingFast size={26} />,
  "Find Account": <HiSearch size={26} />,
  "Customer Online Account Opening": <MdBookOnline size={26} />,
  "Reports": <HiDocumentText size={26} />,
  "Report Count": <HiDocumentReport size={26} />,
  "Account Closure": <HiArchive size={26} />,
  "Superset Reporting": <HiChartPie size={26} />,
  "Retail Account Opening": <HiShoppingBag size={26} />,
}

/* Alternating navy / red from the Global IME Bank logo */
const MODULE_META: Record<string, { description: string; accent: string }> = {
  "New CIF New Account": { description: "Create a new customer file and open a new account", accent: "#1a3282" },
  "New CIF Only": { description: "Create a new customer file only", accent: "#c8181a" },
  "Centralized Corporate Account": { description: "Create corporate account using centralized process", accent: "#1a3282" },
  "RO Account Opening (Branch)": { description: "Open instant accounts with fast-track processing", accent: "#c8181a" },
  "Find Account": { description: "Search and look up existing customer accounts", accent: "#1a3282" },
  "RO Account Opening": { description: "Open instant accounts with fast-track processing", accent: "#1a3282" },
  "Customer Online Account Opening": { description: "Verify and monitor online account opening requests", accent: "#c8181a" },
  "Report": { description: "View account opening status reports", accent: "#1a3282" },
  "Reports": { description: "View account opening status reports", accent: "#1a3282" },
  "Report Count": { description: "View report count and statistical summaries", accent: "#c8181a" },
  "Account Closure": { description: "Raise and track customer account closure requests", accent: "#c8181a" },
  "Superset Reporting": { description: "Open the embedded analytics dashboards", accent: "#1a3282" },
  "Retail Account Opening": { description: "Open retail accounts and track their progress", accent: "#1a3282" },
}

// Administration cards are gated on role rather than on the backend module tree.
// Compared case-insensitively because the role name's casing varies by source
// (e.g. the backend uses both "Bank Admin" and "BANK ADMIN").
const ADMIN_ALLOWED_ROLES = ["superadmin", "developer role", "gibladmin", "bank admin"]

const ADMIN_CARDS: CardDef[] = [
  {
    title: "Apims Logs",
    route: "/log-report",
    icon: <HiDatabase size={26} />,
    description: "Browse APIMS request and response logs across cases",
    accent: "#c8181a",
  },
]

/* The modules Quick Access offers, in display order. A card still only renders
   when the backend permission tree (`state.auth.module.moduleData`) grants the
   module, so this list narrows what is shown -- it never widens it.
   "Report Count" is the one exception: a fixed page, not a backend module. */
const AVAILABLE_MODULES: Array<{ name: string; aliasPrefix?: string }> = [
  { name: "New CIF New Account" },
  { name: "New CIF Only" },
  { name: "Centralized Corporate Account" },
  { name: "RO Account Opening (Branch)" },
  { name: "RO Account Opening" },
  { name: "Retail Account Opening", aliasPrefix: "/retail-account-opening" },
  { name: "Find Account" },
  { name: "Customer Online Account Opening" },
  { name: "Account Closure", aliasPrefix: "/account-closure" },
  { name: "Reports" },
  { name: "Superset Reporting", aliasPrefix: "/superset-report" },
  { name: "Report Count" },
]

/**
 * Locate a whitelisted module in the permission tree. The backend's display
 * name is matched first; `aliasPrefix` is the fallback for modules whose label
 * differs between environments (it matches the module's own alias or any of its
 * children's), so a renamed module still surfaces instead of silently vanishing.
 */
const findGrantedModule = (modules: any[], entry: { name: string; aliasPrefix?: string }) => {
  const byName = modules.find((item: any) => item?.name === entry.name)
  if (byName || !entry.aliasPrefix) return byName

  const prefix = entry.aliasPrefix
  return modules.find((item: any) => {
    const aliases: string[] = [item?.alias, ...(item?.children ?? []).map((c: any) => c?.alias)]
    return aliases.some((alias) => typeof alias === "string" && alias.startsWith(prefix))
  })
}

/**
 * A module's landing route: its own alias, or its first navigable child when it
 * is only a grouping node. Children whose alias carries a route parameter
 * (`/kyc/kyc-update-list/:id`) are detail screens that cannot be opened cold,
 * so they are skipped -- the aside menu filters the same shape out via its own
 * `subRoutesToExclude` list.
 */
const landingRoute = (item: any): string => {
  const children: any[] = Array.isArray(item?.children) ? item.children : []
  const navigableChild = children.find((child) => child?.alias && !child.alias.includes(":"))
  if (navigableChild) return navigableChild.alias
  return item?.alias && !item.alias.includes(":") ? item.alias : ""
}

const Homepage: FC = () => {
  const { mutate } = useGetMasterDataStore()
  const navigate = useHistory()

  const [applicationName, setApplicationName] = useState("")

  const settingTypeData: ISettingTypeState = useSelector((state: any) => state.settingType)
  const module = useSelector((state: any) => state.auth?.module?.moduleData)
  const userName: string = useSelector((state: any) => state.auth?.user?.data?.name ?? "")
  const roleName: string = useSelector((state: any) => state.auth?.rolePermissions?.name ?? "")

  useEffect(() => {
    if (!isEmpty(settingTypeData?.backendData)) {
      const appCred: any[] = settingTypeData?.backendData?.["Application Credentials"] ?? []
      const found: any = appCred.find((item: any) => item?.name === "applicationName")
      if (found) setApplicationName(found.value)
    }
  }, [settingTypeData])

  useEffect(() => { mutate() }, [])

  const cards: CardDef[] = useMemo(() => {
    const modules: any[] = Array.isArray(module) ? module : []

    return AVAILABLE_MODULES.flatMap((entry) => {
      const { name } = entry
      const meta = MODULE_META[name] ?? { description: "", accent: "#1a3282" }

      if (name === "Report Count") {
        return [{ title: name, route: "/report", icon: MODULE_ICON_MAP[name], description: meta.description, accent: meta.accent }]
      }

      const granted = findGrantedModule(modules, entry)
      if (!granted) return []

      const route = landingRoute(granted)
      if (!route) return []

      return [{
        title: name,
        route,
        icon: MODULE_ICON_MAP[name],
        description: meta.description,
        accent: meta.accent,
      }]
    })
  }, [module])

  const canViewAdminCards = ADMIN_ALLOWED_ROLES.includes(roleName.trim().toLowerCase())

  const greeting = useMemo(() => {
    const h = new Date().getHours()
    if (h < 12) return "Good Morning"
    if (h < 17) return "Good Afternoon"
    return "Good Evening"
  }, [])

  const currentDate = useMemo(() =>
    new Date().toLocaleDateString("en-US", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    }), [])

  return (
    <div className="homepage">

      {/* ── Page Header ── */}
      <header className="page-header">
        <div className="page-header-top">
          <span className="header-date">{currentDate}</span>
          {roleName && (
            <span className="role-badge">
              <span className="role-dot" />
              {roleName}
            </span>
          )}
        </div>
        <p className="header-greeting">{greeting}{userName ? `, ${userName}` : ""}!</p>
        <h1 className="header-title">
          Welcome to&nbsp;<span className="header-brand">{applicationName || "Global IME Bank"}</span>
        </h1>
        <div className="header-rule" aria-hidden="true" />
      </header>

      {/* ── Quick Access ── */}
      <div className="section-header">
        <h2 className="section-title">Quick Access</h2>
        <span className="section-divider" />
      </div>

      <section className="card-grid">
        {cards.map((card) => (
          <div
            key={card.route}
            className="home-card"
            style={{ "--card-accent": card.accent } as React.CSSProperties}
            onClick={() => navigate.push(card.route)}
          >
            <div className="card-icon-wrap">
              {card.icon}
            </div>
            <div className="card-body-text">
              <h3 className="card-title">{card.title}</h3>
              <p className="card-desc">{card.description}</p>
            </div>
            <div className="card-arrow">›</div>
          </div>
        ))}
      </section>

      {/* ── Administration (role-gated) ── */}
      {canViewAdminCards && (
        <>
          {/* .section-header only carries a bottom margin, so a second group
              needs its own top spacing to clear the grid above it. */}
          <div className="section-header" style={{ marginTop: 28 }}>
            <h2 className="section-title">Administration</h2>
            <span className="section-divider" />
          </div>

          <section className="card-grid">
            {ADMIN_CARDS.map((card) => (
              <div
                key={card.route}
                className="home-card"
                style={{ "--card-accent": card.accent } as React.CSSProperties}
                onClick={() => {
                  // Pass the dashboard as return_url so the log report can offer
                  // a "Return Back" button (read in logViewer/index.tsx).
                  const returnUrl = window.location.pathname + window.location.search
                  navigate.push(`${card.route}?return_url=${encodeURIComponent(returnUrl)}`)
                }}
              >
                <div className="card-icon-wrap">
                  {card.icon}
                </div>
                <div className="card-body-text">
                  <h3 className="card-title">{card.title}</h3>
                  <p className="card-desc">{card.description}</p>
                </div>
                <div className="card-arrow">›</div>
              </div>
            ))}
          </section>
        </>
      )}

    </div>
  )
}

export { Homepage }
